import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import '../../styles/Dashboard.css';
import Navbar from '../navbar';
import Sidebar from './../sidebar/index';
import Typography from '../../components/Typography';
import CustomButton from '../../components/CustomButton';
import buttonimg from '../../images/Rectangle 4416.png';
import Input from '../../components/Input';
import CustomTextarea from '../../components/CustomTextArea';
import CustomSelect from './../../components/CustomSelect';
import CustomModal from '../../components/CustomModal';

const Setting = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState('success'); // or 'error'
    const [modalMessage, setModalMessage] = useState('');
    const [title, setTitle] = useState('');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        instagram: '',
        occupancy: '',
        gender: '',
        bio: '',
        payoutInfo: { accountName: '', accountNumber: '', bankName: '', bankAddress: '', swift_bicCode: '', iban: '', routingNumber: '' },
        license: '',
        avatar: '',
    });
    const [licenseFile, setLicenseFile] = useState(null);
    const [licensePreviewUrl, setLicensePreviewUrl] = useState(null);
    const [licenseIsPdf, setLicenseIsPdf] = useState(false);
    const [licenseName, setLicenseName] = useState('');
    const [profileFile, setProfileFile] = useState(null);
    const [profilePreviewUrl, setProfilePreviewUrl] = useState(null);
    const [errors, setErrors] = useState({});
    const [submitLoading, setSubmitLoading] = useState(false);
    const [uploadStates, setUploadStates] = useState({
        license: 'idle', // idle, uploading, success, error
        avatar: 'idle',
    });
    const [removeLicense, setRemoveLicense] = useState(false);
    const [removeAvatar, setRemoveAvatar] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [previewContent, setPreviewContent] = useState(null);
    const [previewType, setPreviewType] = useState('image'); // 'image' or 'pdf'

    const toggleSidebar = () => {
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/dashboard`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(response.data);
            } catch (error) {
                localStorage.removeItem('token');
                navigate('/');
            }
        };
        fetchUser();
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        const userInfo = localStorage.getItem('userInfo');
        if (!token || !userInfo) {
            navigate('/login');
            return;
        }

        try {
            const parsedUserInfo = user ? user : {};
            setFormData({
                name: parsedUserInfo.name || '',
                email: parsedUserInfo.email || '',
                phone: parsedUserInfo.phone || '',
                address: parsedUserInfo.address || '',
                instagram: parsedUserInfo.instagram || '',
                occupancy: parsedUserInfo.occupancy || '',
                gender: parsedUserInfo.gender || '',
                bio: parsedUserInfo.bio || '',
                payoutInfo: {
                    accountName: parsedUserInfo.payoutInfo?.accountName || '',
                    accountNumber: parsedUserInfo.payoutInfo?.accountNumber || '',
                    bankName: parsedUserInfo.payoutInfo?.bankName || '',
                    bankAddress: parsedUserInfo.payoutInfo?.bankAddress || '',
                    swift_bicCode: parsedUserInfo.payoutInfo?.swift_bicCode || '',
                    iban: parsedUserInfo.payoutInfo?.iban || '',
                    routingNumber: parsedUserInfo.payoutInfo?.routingNumber || '',
                },
                license: parsedUserInfo.license || '',
                avatar: parsedUserInfo.avatar || '',
            });
            if (parsedUserInfo.license) {
                const isPdf = parsedUserInfo.license.endsWith('.pdf');
                setLicenseIsPdf(isPdf);
                setLicensePreviewUrl(isPdf ? null : `${process.env.REACT_APP_BACKEND_URL}${parsedUserInfo.license}`);
                setLicenseName(parsedUserInfo.license.split('/').pop());
            }
            if (parsedUserInfo.avatar) {
                setProfilePreviewUrl(`${process.env.REACT_APP_BACKEND_URL}${parsedUserInfo.avatar}`);
            }
        } catch (err) {
            setErrors({ general: 'Failed to load user data' });
            navigate('/login');
        }
    }, [navigate, user]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name.includes('payoutInfo.')) {
            const field = name.split('.')[1];
            setFormData((prev) => ({
                ...prev,
                payoutInfo: { ...prev.payoutInfo, [field]: value },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setErrors((prev) => ({ ...prev, license: 'File size must be less than 5MB' }));
                setUploadStates((prev) => ({ ...prev, license: 'error' }));
                return;
            }
            if (!['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)) {
                setErrors((prev) => ({ ...prev, license: 'Only JPEG, PNG, or PDF files are allowed' }));
                setUploadStates((prev) => ({ ...prev, license: 'error' }));
                return;
            }
            setLicenseFile(file);
            setRemoveLicense(false);
            setErrors((prev) => ({ ...prev, license: '' }));
            setUploadStates((prev) => ({ ...prev, license: 'success' }));

            const isPdf = file.type === 'application/pdf';
            setLicenseIsPdf(isPdf);
            setLicenseName(file.name);
            if (!isPdf) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setLicensePreviewUrl(reader.result);
                };
                reader.readAsDataURL(file);
            } else {
                setLicensePreviewUrl(null);
            }
        } else {
            setLicenseFile(null);
            setRemoveLicense(true);
            setLicensePreviewUrl(null);
            setLicenseIsPdf(false);
            setLicenseName('');
            setErrors((prev) => ({ ...prev, license: '' }));
            setUploadStates((prev) => ({ ...prev, license: 'idle' }));
        }
    };

    const handleProfileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setErrors((prev) => ({ ...prev, avatar: 'File size must be less than 5MB' }));
                setUploadStates((prev) => ({ ...prev, avatar: 'error' }));
                return;
            }
            if (!['image/jpeg', 'image/png'].includes(file.type)) {
                setErrors((prev) => ({ ...prev, avatar: 'Only JPEG or PNG files are allowed' }));
                setUploadStates((prev) => ({ ...prev, avatar: 'error' }));
                return;
            }
            setProfileFile(file);
            setRemoveAvatar(false);
            setErrors((prev) => ({ ...prev, avatar: '' }));
            setUploadStates((prev) => ({ ...prev, avatar: 'success' }));

            const reader = new FileReader();
            reader.onloadend = () => {
                setProfilePreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        } else {
            setProfileFile(null);
            setRemoveAvatar(true);
            setProfilePreviewUrl(null);
            setErrors((prev) => ({ ...prev, avatar: '' }));
            setUploadStates((prev) => ({ ...prev, avatar: 'idle' }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name || formData.name.length < 2) {
            newErrors.name = 'Fullname is required and must be at least 2 characters';
        }
        if (formData.bio.length > 2000) {
            newErrors.bio = 'Bio must not exceed 2000 characters';
        }
        if (formData.phone && !/^\+?[1-9]\d{1,14}$/.test(formData.phone)) {
            newErrors.phone = 'Invalid phone number format';
        }
        if (
            formData.payoutInfo.accountNumber &&
            formData.payoutInfo.accountNumber.length < 4
        ) {
            newErrors['payoutInfo.accountNumber'] = 'Account number must be at least 4 characters';
        }

        if (!formData.occupancy) {
            newErrors.occupancy = 'Occupancy is required';
        }

        if (!formData.address) {
            newErrors.address = 'Address is required';
        }

        if (!formData.gender) {
            newErrors.gender = 'Gender is required';
        }

        if (!formData.instagram) {
            newErrors.instagram = 'Instagram is required';
        }

        if (!formData.payoutInfo.accountName || !formData.payoutInfo.bankName || !formData.payoutInfo.bankAddress || !formData.payoutInfo.accountNumber || !formData.payoutInfo.swift_bicCode || !formData.payoutInfo.routingNumber) {
            newErrors.payoutInfo = 'Payout info is required';
        }

        if (!licenseFile && (removeLicense || !formData.license)) {
            newErrors.license = 'License is required';
        }

        if (!profileFile && (removeAvatar || !formData.avatar)) {
            newErrors.avatar = 'Avatar is required';
        }

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        setSubmitLoading(true);
        setUploadStates({ license: 'uploading', avatar: 'uploading' });
        try {
            const token = localStorage.getItem('token');
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('phone', formData.phone);
            formDataToSend.append('address', formData.address);
            formDataToSend.append('instagram', formData.instagram);
            formDataToSend.append('occupancy', formData.occupancy);
            formDataToSend.append('gender', formData.gender);
            formDataToSend.append('bio', formData.bio);
            formDataToSend.append('payoutInfo', JSON.stringify(formData.payoutInfo));
            if (licenseFile) {
                formDataToSend.append('license', licenseFile);
            } else if (removeLicense) {
                formDataToSend.append('removeLicense', true);
            }
            if (profileFile) {
                formDataToSend.append('avatar', profileFile);
            } else if (removeAvatar) {
                formDataToSend.append('removeAvatar', true);
            }

            const response = await axios.put(`${process.env.REACT_APP_BACKEND_URL}/api/auth/settings`, formDataToSend, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            // Update localStorage with new user data
            localStorage.setItem('userInfo', JSON.stringify(response.data));
            setUser(response.data);
            setFormData((prev) => ({ ...prev, license: response.data.license || '', avatar: response.data.avatar || '' }));

            console.log(response.data);

            const newLicense = response.data.license;
            if (newLicense) {
                const isPdf = newLicense.endsWith('.pdf');
                setLicenseIsPdf(isPdf);
                setLicensePreviewUrl(isPdf ? null : `${process.env.REACT_APP_BACKEND_URL}${newLicense}`);
                setLicenseName(newLicense.split('/').pop());
            } else {
                setLicensePreviewUrl(null);
                setLicenseIsPdf(false);
                setLicenseName('');
            }

            const newAvatar = response.data.avatar;
            if (newAvatar) {
                setProfilePreviewUrl(`${process.env.REACT_APP_BACKEND_URL}${newAvatar}`);
            } else {
                setProfilePreviewUrl(null);
            }

            setLicenseFile(null);
            setProfileFile(null);
            setRemoveLicense(false);
            setRemoveAvatar(false);
            setTitle('SETTINGS SAVED!');
            setModalType('success');
            setModalMessage('All your settings are in place. You can begin investing anytime.');
            setShowModal(true);
            setUploadStates({ license: 'success', avatar: 'success' });
            setSubmitLoading(false);
        } catch (err) {
            setErrors({ general: err.response?.data?.message || 'Failed to update settings' });
            setSubmitLoading(false);
            setTitle('OH SNAP!');
            setModalType('error');
            setModalMessage('Oops! We ran into an issue saving your settings. Try again please.');
            setShowModal(true);
            setUploadStates({ license: 'error', avatar: 'error' });
            if (err.response?.status === 401) {
                localStorage.removeItem('token');
                localStorage.removeItem('userInfo');
                navigate('/login');
            }
        }
    };

    const handleLicenseClick = () => {
        if (submitLoading) return;
        let url;
        if (licenseFile) {
            url = URL.createObjectURL(licenseFile);
        } else if (formData.license && !removeLicense) {
            url = `${process.env.REACT_APP_BACKEND_URL}${formData.license}`;
        }
        if (url) {
            setPreviewContent(url);
            setPreviewType(licenseIsPdf ? 'pdf' : 'image');
            setShowPreviewModal(true);
        }
    };

    const handleProfileClick = () => {
        if (submitLoading) return;
        let url;
        if (profileFile) {
            url = URL.createObjectURL(profileFile);
        } else if (formData.avatar && !removeAvatar) {
            url = `${process.env.REACT_APP_BACKEND_URL}${formData.avatar}`;
        }
        if (url) {
            setPreviewContent(url);
            setPreviewType('image');
            setShowPreviewModal(true);
        }
    };

    const getUploadStateClass = (state) => {
        switch (state) {
            case 'uploading':
                return 'border-blue-500 animate-pulse';
            case 'success':
                return 'border-green-500';
            case 'error':
                return 'border-red-500';
            default:
                return 'border-[#262626]';
        }
    };

    const getMicrocopy = (field, state) => {
        if (state === 'uploading') return 'Uploading...';
        if (state === 'success') return 'Uploaded successfully';
        if (state === 'error') return 'Upload failed';
        return field === 'license' ? 'Drag & drop or click to upload license (JPEG, PNG, PDF)' : 'Drag & drop or click to upload photo (JPEG, PNG)';
    };

    return (
        <div>
            <Navbar user={user} isSidebarOpen={isSidebarOpen}
                toggleSidebar={toggleSidebar}
                closeSidebar={closeSidebar} />
            <div className="flex"  >
                <Sidebar isOpen={isSidebarOpen} closeSidebar={closeSidebar} />
                {showModal && (
                    <CustomModal
                        type={modalType}
                        title={title}
                        message={modalMessage}
                        onClose={() => setShowModal(false)}
                    />
                )}
                {showPreviewModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setShowPreviewModal(false)}>
                        <div className="bg-[#121212] p-4 rounded-lg max-w-4xl max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                            {previewType === 'image' ? (
                                <img src={previewContent} alt="Preview" className="max-w-full max-h-[80vh]" />
                            ) : (
                                <embed src={previewContent} type="application/pdf" className="w-[80vw] h-[80vh]" />
                            )}
                            {/* <button onClick={() => setShowPreviewModal(false)} className="mt-4 bg-red-500 text-white px-4 py-2 rounded">Close</button> */}
                        </div>
                    </div>
                )}
                <div className="w-full h-full min-h-screen sm:p-5 bg-[#121212] border-[#353535] border-l">
                    <div className="w-full p-5 rounded-[4px] bg-black ">
                        <form onSubmit={handleSubmit} className="">
                            <div className='grid relative grid-cols-8'>
                                <div className='lg:col-span-7 col-span-8'>
                                    <Typography text={'Settings'} transform={'uppercase'} color={'#FFF'} size={'16px'} weight={400} lineheight={'170%'} family={'Supply'} />
                                    {errors.general && (
                                        <div className="text-red-500 mb-4">{errors.general}</div>
                                    )}

                                    <div className='grid sm:grid-cols-3 mt-[50px] gap-[35px]'>
                                        <div className='col-span-1'>
                                            <Input
                                                label="Full name"
                                                placeholder="ENTER NAME"
                                                required
                                                type="text"
                                                name="name"
                                                id="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                borderLeft
                                                borderRight
                                            />
                                            {errors.name && <div className="text-red-500 text-sm">{errors.name}</div>}
                                        </div>
                                        <div className='col-span-1'>
                                            <Input
                                                label="Email"
                                                placeholder="ENTER EMAIL"
                                                type="text"
                                                name="email"
                                                required
                                                id="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                disabled
                                                borderLeft
                                                borderRight
                                            />
                                            {errors.email && <div className="text-red-500 text-sm">{errors.email}</div>}
                                        </div>
                                        <div className='col-span-1'>
                                            <Input
                                                label="Phone"
                                                placeholder="ENTER PHONE"
                                                type="text"
                                                required
                                                name="phone"
                                                id="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                borderLeft
                                                borderRight
                                            />
                                            {errors.phone && <div className="text-red-500 text-sm">{errors.phone}</div>}
                                        </div>
                                    </div>
                                    <div className='grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-[35px] mt-[23px]'>
                                        <div className='lg:col-span-2 col-span-1'>
                                            <Input
                                                label="Address"
                                                placeholder="ENTER ADDRESS"
                                                type="text"
                                                required
                                                name="address"
                                                id="address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                borderLeft
                                                borderRight
                                            />
                                            {errors.address && <div className="text-red-500 text-sm">{errors.address}</div>}
                                        </div>
                                        <div className='col-span-1'>
                                            <Input
                                                label="Instagram"
                                                placeholder="ENTER INSTAGRAM"
                                                type="text"
                                                name="instagram"
                                                required
                                                id="instagram"
                                                value={formData.instagram}
                                                onChange={handleChange}
                                                borderLeft
                                                borderRight
                                            />
                                            {errors.instagram && <div className="text-red-500 text-sm">{errors.instagram}</div>}
                                        </div>
                                    </div>
                                    <div className='grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-[35px] mt-[23px]'>
                                        <div className='lg:col-span-2 col-span-1'>
                                            <Input
                                                label="Occupancy"
                                                placeholder="ENTER OCCUPANCY"
                                                type="text"
                                                name="occupancy"
                                                id="occupancy"
                                                value={formData.occupancy}
                                                onChange={handleChange}
                                                required
                                                borderLeft
                                                borderRight
                                            />
                                        </div>
                                        <div className='col-span-1'>
                                            <CustomSelect
                                                label="Gender"
                                                name="gender"
                                                id="gender"
                                                value={formData.gender}
                                                onChange={handleChange}
                                                options={[
                                                    { value: '', label: 'Select Gender' },
                                                    { value: 'male', label: 'Male' },
                                                    { value: 'female', label: 'Female' },
                                                ]}
                                                borderLeft
                                                borderRight
                                            />
                                        </div>
                                    </div>
                                    <CustomTextarea
                                        label="Your Bio"
                                        placeholder="Write your Bio"
                                        name="bio"
                                        id="bio"
                                        value={formData.bio}
                                        onChange={handleChange}
                                        maxLength="2000"
                                    />
                                    {errors.bio && <div className="text-red-500 text-sm">{errors.bio}</div>}
                                    <div className="mt-8 lg:max-w-[712px] border-[#262626] border-[1px]">
                                        <header className="h-[51px] px-6 flex justify-between items-center bg-[#333]">
                                            <Typography
                                                className=""
                                                color="#FFF"
                                                size="16px"
                                                family="Supply"
                                                weight={400}
                                                lineheight="170%"
                                                transform="uppercase"
                                                text="PAYOUT INFO"
                                            />
                                        </header>
                                        <div className="px-6 py-5 relative bg-black">
                                            <div className="grid sm:grid-cols-3 grid-cols-1 sm:gap-24">
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.accountName'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'Recipient Name:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.accountName'}
                                                                id={'payoutInfo.accountName'}
                                                                value={formData.payoutInfo.accountName}
                                                                onChange={handleChange}
                                                                placeholder={'Account Name'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.bankName'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'Bank Name:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.bankName'}
                                                                id={'payoutInfo.bankName'}
                                                                value={formData.payoutInfo.bankName}
                                                                onChange={handleChange}
                                                                placeholder={'Bank Name'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.bankAddress'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'Bank Address:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.bankAddress'}
                                                                id={'payoutInfo.bankAddress'}
                                                                value={formData.payoutInfo.bankAddress}
                                                                onChange={handleChange}
                                                                placeholder={'Bank Address'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid sm:grid-cols-3 grid-cols-1 sm:gap-24">
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.accountNumber'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'Account Number:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.accountNumber'}
                                                                id={'payoutInfo.accountNumber'}
                                                                value={formData.payoutInfo.accountNumber}
                                                                onChange={handleChange}
                                                                placeholder={'Account Number'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.swift_bicCode'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'SWIFT/BIC Code:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.swift_bicCode'}
                                                                id={'payoutInfo.swift_bicCode'}
                                                                value={formData.payoutInfo.swift_bicCode}
                                                                onChange={handleChange}
                                                                placeholder={'SWIFT/BIC Code'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.iban'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'IBAN:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.iban'}
                                                                id={'payoutInfo.iban'}
                                                                value={formData.payoutInfo.iban}
                                                                onChange={handleChange}
                                                                placeholder={'IBAN'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="grid sm:grid-cols-3 grid-cols-1 sm:gap-24">
                                                <div className='col-span-1'>
                                                    <div className="input-container">
                                                        <label htmlFor={'payoutInfo.routingNumber'} className="input-label flex justify-between">
                                                            <Typography size="12px" color="#FFF" lineheight={'170%'} opacity={.6} text={'Routing Number:'} />
                                                        </label>
                                                        <div className={`flex items-center border-b border-white border-opacity-10`}>
                                                            <input
                                                                type={'text'}
                                                                name={'payoutInfo.routingNumber'}
                                                                id={'payoutInfo.routingNumber'}
                                                                value={formData.payoutInfo.routingNumber}
                                                                onChange={handleChange}
                                                                placeholder={'Routing Number'}
                                                                style={{ '::placeholder': { color: '#242424', fontColor: '#242424' }, fontSize: '14px', fontFamily: 'Aeonik' }}
                                                                className={`input-field h-[30px] custom-normal-text placeholder:text-[#242424] placeholder:font-[14px]`}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            {errors.payoutInfo && <div className="text-red-500 text-sm">{errors.payoutInfo}</div>}
                                        </div>
                                    </div>
                                    <div className="grid lg:grid-cols-3 md:grid-cols-2 grid-cols-1 sm:gap-[82px] sm:mt-24 gap-[42px] mt-[42px]">
                                        <div className='col-span-1 '>
                                            <div className="custom-file-input-container">
                                                <div className="custom-file-input-label">
                                                    <Typography size="14px" color="#FFF" text={'Upload License'} />
                                                </div>
                                                <div className={`bg-[#333] rounded-xl p-6 border-2 ${getUploadStateClass(uploadStates.license)} shadow-lg flex flex-col justify-center items-center h-48 transition-all duration-300 cursor-pointer`} onClick={handleLicenseClick}>
                                                    {(licenseFile || (formData.license && !removeLicense)) ? (
                                                        <div className="relative w-full h-full flex justify-center items-center">
                                                            {licenseIsPdf ? (
                                                                <div className="text-white text-center flex flex-col items-center">
                                                                    <svg className="w-12 h-12 mb-2" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" />
                                                                    </svg>
                                                                    PDF Document
                                                                    <br />
                                                                    {licenseName}
                                                                </div>
                                                            ) : (
                                                                <img src={licensePreviewUrl} alt="License Preview" className="max-w-full max-h-full object-contain rounded-md" />
                                                            )}
                                                            {!submitLoading && (
                                                                <button
                                                                    type="button"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setLicenseFile(null);
                                                                        setRemoveLicense(true);
                                                                        setLicensePreviewUrl(null);
                                                                        setLicenseIsPdf(false);
                                                                        setLicenseName('');
                                                                        setUploadStates((prev) => ({ ...prev, license: 'idle' }));
                                                                    }}
                                                                    className="absolute top-2 right-2 bg-red-600 text-white px-2 py-1 rounded-md text-xs hover:bg-red-700 transition"
                                                                >
                                                                    Remove
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <div className="text-gray-400 text-center text-sm">
                                                            No license uploaded
                                                            <br />
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="file"
                                                    onChange={handleFileChange}
                                                    accept="image/jpeg,image/png,application/pdf"
                                                    className="mt-3 block w-full text-sm text-gray-300 file:mr-4 file:py-2.5 file:px-5 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#262626] file:text-white hover:file:bg-[#3a3a3a] cursor-pointer transition"
                                                    disabled={submitLoading}
                                                />
                                                {errors.license && <div className="text-red-500 text-sm mt-2">{errors.license}</div>}
                                                <div className='mt-3 text-gray-500 text-xs'>
                                                    {getMicrocopy('avatar', uploadStates.license)}
                                                </div>
                                                <div className='mt-[13px] '>
                                                    <Typography size="14px" color="#DF0F11" family={'Supply'} transform={'uppercase'} text="*All fields are required to start your investment." />
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-span-1'>
                                            <div className="custom-file-input-container">
                                                <div className="custom-file-input-label">
                                                    <Typography size="14px" color="#FFF" text={'Upload Profile Photo'} />
                                                </div>
                                                <div className={`flex flex-col items-center ${submitLoading ? 'opacity-70' : ''} transition-opacity`}>
                                                    <div className={`w-32 h-32 rounded-full overflow-hidden border-4 ${getUploadStateClass(uploadStates.avatar)} shadow-xl bg-[#333] flex items-center mt-4 justify-center mb-4 transition-all duration-300 cursor-pointer`} onClick={handleProfileClick}>
                                                        {(profileFile || (formData.avatar && !removeAvatar)) ? (
                                                            <img
                                                                src={profilePreviewUrl}
                                                                alt="Profile Preview"
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="text-gray-400 text-center text-sm px-4">No photo</div>
                                                        )}
                                                    </div>
                                                    {(profileFile || (formData.avatar && !removeAvatar)) && !submitLoading && (
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setProfileFile(null);
                                                                setRemoveAvatar(true);
                                                                setProfilePreviewUrl(null);
                                                                setUploadStates((prev) => ({ ...prev, avatar: 'idle' }));
                                                            }}
                                                            className="text-red-600 text-sm mb-3 hover:text-red-700 transition"
                                                        >
                                                            Remove Photo
                                                        </button>
                                                    )}
                                                </div>
                                                <input
                                                    type="file"
                                                    onChange={handleProfileUpload}
                                                    accept="image/jpeg,image/png"
                                                    className="mt-3 block w-full text-sm text-gray-300 file:mr-4 file:py-2.5 file:px-5 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#262626] file:text-white hover:file:bg-[#3a3a3a] cursor-pointer transition"
                                                    disabled={submitLoading}
                                                />
                                                {errors.avatar && <div className="text-red-500 text-sm mt-2">{errors.avatar}</div>}
                                                <div className='mt-3 text-gray-500 text-xs'>
                                                    {getMicrocopy('avatar', uploadStates.avatar)}
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                                <div className='lg:col-span-1 absolute lg:relative top-0 right-0 flex justify-center items-start'>
                                    <CustomButton
                                        type="submit"
                                        className='w-[95px] h-8'
                                        background={<img src={buttonimg} alt='buttonimg' className="button-svg" width="100%" height="100%" />}
                                        text={submitLoading ? 'Saving...' : 'Save'}
                                        disabled={submitLoading}
                                    />
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Setting;